import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	InspectionTemplate,
	InspectionTemplateColumn,
	InspectionTemplateDraft,
	InspectionTemplateSection,
	InspectionTemplatesData,
} from "../models/Inspection";
import { InspectionSection } from "../models/InspectionSection";

const moment = getMomentTz();

export const getInspectionTemplates = async (
	params: {
		templateId?: number | null;
		sectionId?: number | null;
		includeInactive?: boolean;
	} = {},
	signal?: AbortSignal,
): Promise<InspectionTemplatesData> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: params.templateId ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "IncludeInactive", value: params.includeInactive ?? false },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspectionTemplates]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		return {
			templates: (get(tables, "0.rows", []) as InspectionTemplate[]).map(
				(row) => ({
					...row,
					EffectiveFrom: row.EffectiveFrom
						? moment(
								moment.utc(row.EffectiveFrom).format("YYYY-MM-DD"),
							).toDate()
						: null,
				}),
			),
			columns: get(tables, "1.rows", []) as InspectionTemplateColumn[],
			sections: (get(tables, "2.rows", []) as InspectionTemplateSection[]).map(
				(row) => ({
					...row,
					EffectiveFrom: row.EffectiveFrom
						? moment(
								moment.utc(row.EffectiveFrom).format("YYYY-MM-DD"),
							).toDate()
						: null,
				}),
			),
		};
	} else {
		throw new Error(resp.message || "Error fetching inspection templates");
	}
};

export const getInspectionSections = async (
	params: { shiftId: number; userId?: number | null },
	signal?: AbortSignal,
): Promise<InspectionSection[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
		{ name: "UserId", value: params.userId ?? null },
	];

	const resp = await apiService.callV2(
		"[M1].[GetInspectionSections]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return (get(resp, "data.tables[0].rows", []) as InspectionSection[]).map(
			(row) => ({
				...row,
				ShiftEventAt: row.ShiftEventAt
					? moment.utc(row.ShiftEventAt).toDate()
					: null,
				LastRolledAt: row.LastRolledAt
					? moment.utc(row.LastRolledAt).toDate()
					: null,
				NextPlannedAt: row.NextPlannedAt
					? moment.utc(row.NextPlannedAt).toDate()
					: null,
			}),
		);
	} else {
		throw new Error(resp.message || "Error fetching the sections");
	}
};

export const upsertInspectionTemplate = async (
	draft: InspectionTemplateDraft,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: draft.TemplateId },
		{ name: "SectionId", value: draft.SectionId },
		{ name: "Rev", value: draft.Rev },
		{
			name: "EffectiveFrom",
			value: draft.EffectiveFrom
				? moment(draft.EffectiveFrom).format("YYYY-MM-DD")
				: null,
		},
		{ name: "UploadFileId", value: draft.UploadFileId },
		{ name: "IsActive", value: true },
		{
			name: "ColumnsJson",
			value: JSON.stringify(
				draft.Columns.map((column, index) => ({
					ColumnId: column.ColumnId,
					GroupName: column.GroupName,
					ColumnLabel: column.ColumnLabel,
					ValueTypeCode: column.ValueTypeCode,
					SortOrder: index + 1,
				})),
			),
		},
	];

	const resp = await apiService.callV2(
		"[M1].[UpsertInspectionTemplate]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving the inspection template");
	}
};

export const deleteInspectionTemplate = async (templateId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "TemplateId", value: templateId }];

	const resp = await apiService.callV2(
		"[M1].[DeleteInspectionTemplate]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the inspection template");
	}
};
